import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { UpdateDatum } from '@core/models/admin/account/update-approvals';
import { BankListDatum, CommonService } from '@core/services/common.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { LoadingService } from '@core/services/loading.service';
import { UpdateApprovalService } from '@core/services/update-approvals';
import { Store } from '@ngxs/store';
import { ButtonDirective } from '@shared/directives/button.directive';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { TableActions } from 'app/auth/table-store/table.actions';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CamelToTitlePipe } from '@shared/pipes/camel-to-title.pipe';

@Component({
  selector: 'app-updates-approvals-details',
  standalone: true,
  imports: [TableSkeletonLoaderComponent, CommonModule, ButtonDirective, CamelToTitlePipe],
  templateUrl: './updates-approvals-details.component.html',
  styleUrl: './updates-approvals-details.component.scss',
})
export class UpdatesApprovalsDetailsComponent implements OnInit {
  isFetchingUpdateDetails$!: Observable<boolean>;
  updateDetailsSubject = new BehaviorSubject<any>(null);
  updateDetails$ = this.updateDetailsSubject.asObservable();
  updateId!: string;
  editedByName: string = '';
  subs: Subscription[] = [];
  bankList: BankListDatum[] = [];

  overallStatus: 'pending' | 'approved' | 'rejected' = 'pending';

  updateApprovalService = inject(UpdateApprovalService);
  activatedRoute = inject(ActivatedRoute);
  toast = inject(NotificationFacade);
  loadingService = inject(LoadingService);
  confirmDialogService = inject(ConfirmDialogService);
  store = inject(Store);
  commonService = inject(CommonService);

  constructor() {
    this.isFetchingUpdateDetails$ = this.loadingService.getLoadingObservable(
      'get-updates-value-by-combined-id'
    );
  }

  ngOnInit(): void {
    this.updateId = this.activatedRoute.snapshot.queryParams['updateId'];
    this.editedByName = this.activatedRoute.snapshot.queryParams['editedBy'] || '';
    this.getBankList();
    this.getUpdateDetails(this.updateId);
  }

  goBack() {
    history.back();
  }

  getUpdateDetails(updateId: string) {
    const sub = this.updateApprovalService
      .getUpdateApprovalDetails(updateId)
      .subscribe({
        next: (response) => {
          const formattedDetails = response.data.map((item: any) => {
          if (item.column_name === 'BANK_CODE') {
            return {
              ...item,
              column_name: 'BANK_NAME',
              old_value: this.getBankName(item.old_value),
              new_value: this.getBankName(item.new_value)
            };
          }
          return item;
        });

        this.updateDetailsSubject.next(formattedDetails);
        const statuses = formattedDetails.map((update: any) => update.status?.toLowerCase());
          if (statuses.includes('rejected')) {
            this.overallStatus = 'rejected';
          } else if (statuses.every(s => s === 'approved')) {
            this.overallStatus = 'approved';
          } else {
            this.overallStatus = 'pending';
          }
        },
        error: (error) => {
          this.toast.error('Error fetching update details. Please try again.');
        },
      });
    this.subs.push(sub);
  }

  updateAction(action: 'approved' | 'rejected') {
    this.confirmDialogService.openDialog({
      isOpen: true,
      isDialogLoading: false,
      title: `Yes, ${action === 'approved' ? 'Approve' : 'Reject'} Update`,
      description: `Are you sure you want to ${action === 'approved' ? 'approve' : 'rejected'} this update?`,
      loadingText: 'Approving Update, please wait...',
      showQuestionGif: true,
      action: () => {
        this.confirmApproval(action);
      },
    });
  }
  confirmApproval(action: 'approved' | 'rejected') {
    const sub = this.store
      .dispatch(
        new TableActions.ApproveUpdate({
          updateId: this.updateId,
          action: action,
        })
      )
      .subscribe({
        complete: () => {
          this.toast.success(
            `Update ${action === 'approved' ? 'approved' : 'rejected'} successfully`
          );
          this.confirmDialogService.updateDialog({
            isDialogLoading: false,
            isOpen: false,
          });
          // call metadata refresh
          this.store.dispatch(new TableActions.GetUpdatesMetadata());
          setTimeout(() => this.getUpdateDetails(this.updateId), 500);
        },
        error: (error) => {
          this.toast.error('Error approving update. Please try again.');
          this.confirmDialogService.updateDialog({ isDialogLoading: false });
        },
      });
  }
    getValue(update: any, key: 'old_value' | 'new_value' = 'old_value') {
    const dateFields = [
      'dob',
      'permit_expiry_date',
      'permit_issue_date',
      'id_expiry_date',
      'id_issue_date',
      'nok_dob',
      'issue_date',
      'expiry_date',
    ];
    if (dateFields.includes(update.column_name)) {
      return update[key]
        ? new Date(update[key]).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          })
        : null;
    }
    return update[key];
  }

  getBankList() {
    const sub = this.commonService.getBankList().subscribe({
      next: (res) => {
        this.bankList = res.data;
      },
      error: (error) => {
        console.error('Error fetching bank list:', error);
      },
    });
    this.subs.push(sub);
  }

  getBankName(bankCode: string): string {
    const bank = this.bankList.find(b => b.bankCode === bankCode);
    return bank ? bank.bankName : bankCode; 
  }

  getProfileNumber(update: UpdateDatum): string {
    if (update.joint_account) {
      const jointAccount = update.joint_account as any;
      
      // Check if joint_kyc array exists and has profiles
      if (jointAccount.joint_kyc && Array.isArray(jointAccount.joint_kyc)) {
        // Profile 1 is always joint_kyc[0]
        if (jointAccount.joint_kyc[0]?.account_id === update.account_id) {
          return '1';
        }
        // Profile 2 is joint_kyc[1] if it exists
        if (jointAccount.joint_kyc[1]?.account_id === update.account_id) {
          return '2';
        }
      } else if (jointAccount.personal_details && update.account?.user_profile) {
        // Compare the personal details to determine which profile is being edited
        // If they match, it's Profile 1, otherwise it's Profile 2
        const jointProfile = jointAccount.personal_details;
        const editingProfile = update.account.user_profile;
        
        // Check if the editing profile matches the joint account's first profile
        const isProfile1 = 
          jointProfile.email === editingProfile.email ||
          (jointProfile.first_name === editingProfile.first_name && 
           jointProfile.last_name === editingProfile.last_name);
        
        return isProfile1 ? '1' : '2';
      } else if (jointAccount.personal_details) {
        // Fallback: if we only have personal_details, assume profile 1
        return '1';
      }
      
      return '';
    } else if (update.account) {
      // For regular accounts, there's only one profile
      return '1';
    }
    return '';
  }

  hasJointAccount(): boolean {
    const updates = this.updateDetailsSubject.getValue();
    if (!updates || !Array.isArray(updates)) {
      return false;
    }
    return updates.some(update => update.joint_account);
  }

  getEditedBy(): string {
    return this.editedByName || '-';
  }

  getUserName(update: UpdateDatum) {
    if (update.joint_account?.personal_details) {
      const { first_name, middle_name, last_name } = update.joint_account.personal_details;
      return [first_name, middle_name, last_name].filter(Boolean).join(" ");
    } else if (update.account?.user_profile) {
      const { first_name, middle_name, last_name } = update.account.user_profile;
      return [first_name, middle_name, last_name].filter(Boolean).join(" ");
    }
    return '';
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
